import { useCallback, useState, useRef, useEffect } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { filterCategories } from 'components/MenuManagement/helpers/categoryManagement';
import { MenuData } from 'components/MenuManagement/types';

const ACTIVE_CATEGORY_OFFSET = 150;
const STICKY_HEADER_HEIGHT = 100;

export default function useCategoryManagement(categories: string[], menu: MenuData) {
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);
  const [activeCategoryID, setActiveCategoryID] = useState<string>('');

  const derivedFilteredCategories = filterCategories(categories, menu, debouncedSearchQuery);

  const handleCategorySelect = (categoryID: string | null) => {
    setActiveCategoryID(categoryID || '');

    if (!categoryID || !categoryRefs.current[categoryID]) {
      return;
    }

    const categoryElement = categoryRefs.current[categoryID];
    const categoryPosition = categoryElement.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = categoryPosition - STICKY_HEADER_HEIGHT;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  // kept memoized: react-hooks/exhaustive-deps is not compiler-aware and this drives an effect
  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY + ACTIVE_CATEGORY_OFFSET;
    let closestCategory: string | null = null;
    let minDistance = Infinity;

    Object.keys(categoryRefs.current).forEach(categoryID => {
      const categoryElement = categoryRefs.current[categoryID];
      if (categoryElement) {
        const categoryTop = categoryElement.getBoundingClientRect().top + window.scrollY;
        const distance = Math.abs(scrollPosition - categoryTop);

        if (distance < minDistance) {
          minDistance = distance;
          closestCategory = categoryID;
        }
      }
    });

    if (closestCategory) {
      setActiveCategoryID(closestCategory);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const resetSearch = () => {
    setSearchQuery('');
  };

  return {
    categoryRefs,
    filteredCategories: derivedFilteredCategories,
    searchQuery,
    activeCategoryID,
    setSearchQuery,
    handleCategorySelect,
    resetSearch,
  };
}
